import React, { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';

import { AppContextProvider } from '@contexts/AppContext';
import useEventTracking from '@hooks/useEventTracking';
import { ContentType, Page, TrackingLocation } from '@tracking';

import { AppContextOrgChartsProps, OrgChartCompanyProps } from '../interfaces';
import OrgChartBottomPaywall from './BottomPaywall';
import OrgChartCompanyHeader from './CompanyHeader';
import OrgChartCompanyTree from './CompanyTree';
import MoreOrgCharts from './MoreOrgCharts';
import { OrgChartProvider } from './OrgChartContext';
import OrgChartCompanyRightRail from './RightRail/CompanyRightRail';

export type OrgChartCompanyPageProps = AppContextOrgChartsProps & OrgChartCompanyProps;

const queryClient = new QueryClient();

const OrgChartCompanyPage = ({
  env,
  id,
  initialCurrentUser,
  isGdprRequired,
  isWebview,
  title,
  description,
  authors,
  lastUpdated,
  slug,
  employees,
  quickFacts,
  articles,
  directors,
  moreOrgCharts,
}: OrgChartCompanyPageProps) => {
  const { Track } = useEventTracking({
    contentId: id,
    contentType: ContentType.OrgChart,
    location: TrackingLocation.OrgChartCompanyPage,
    page: Page.OrgChart,
    pageId: id,
  });

  const shouldShowPaywall = !initialCurrentUser || !initialCurrentUser.is_pro_subscriber;

  // Lock scrolling when paywall is shown
  useEffect(() => {
    if (shouldShowPaywall) {
      // Add styles to prevent scrolling
      document.body.style.position = 'fixed';
      document.body.style.top = '0';
      document.body.style.width = '100%';
      window.scrollTo(0, 0);

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, 0);
      };
    }
  }, [shouldShowPaywall]);

  return (
    <AppContextProvider
      env={env}
      initialCurrentUser={initialCurrentUser}
      isGdprRequired={isGdprRequired}
      isWebview={isWebview}
    >
      <QueryClientProvider client={queryClient}>
        <OrgChartProvider employees={employees}>
          <Track>
            <div className="relative px-16 sm:px-28">
              <div className="m-auto max-w-container-lg">
                <div className="flex flex-col md:flex-row">
                  <div className="w-full md:max-w-[740px] md:pr-28 lg:max-w-[940px] lg:pr-56">
                    <OrgChartCompanyHeader
                      title={title}
                      description={description}
                      authors={authors}
                      lastUpdated={lastUpdated}
                      user={initialCurrentUser}
                      slug={slug}
                    />
                    <OrgChartCompanyTree />
                  </div>
                  <OrgChartCompanyRightRail
                    quickFacts={quickFacts}
                    recommendedArticles={articles}
                    directors={directors}
                  />
                </div>
                {!shouldShowPaywall && <MoreOrgCharts companies={moreOrgCharts} />}
              </div>
            </div>

            {shouldShowPaywall && (
              <OrgChartBottomPaywall
                currentUser={initialCurrentUser}
                shouldShowPaywall={shouldShowPaywall}
              />
            )}
          </Track>
        </OrgChartProvider>
      </QueryClientProvider>
    </AppContextProvider>
  );
};

export default OrgChartCompanyPage;
